import React, { useState, useEffect } from 'react';
import { SegmentWithUI } from '../types/timeline';
import { generateEDL } from '../utils/edlGenerator';

interface ExportDialogProps {
    isOpen: boolean;
    onClose: () => void;
    segments: SegmentWithUI[];
}

export const ExportDialog: React.FC<ExportDialogProps> = ({ isOpen, onClose, segments }) => {
    const [mode, setMode] = useState<'video' | 'conform'>('video'); // Export mode
    const [isExporting, setIsExporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);

    // Export Settings (Video)
    const [resolution, setResolution] = useState('1080p');
    const [fps, setFps] = useState('30');
    const [aiUpscale, setAiUpscale] = useState(false);
    const [aiUpscaleAvailable, setAiUpscaleAvailable] = useState(false);

    // Export Settings (Conform)
    const [edlFps, setEdlFps] = useState('25');

    // Check AI upscale availability on mount
    useEffect(() => {
        if (isOpen) {
            fetch('/api/video/ai-upscale-status')
                .then(r => r.json())
                .then(data => setAiUpscaleAvailable(data.available))
                .catch(() => setAiUpscaleAvailable(false));
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const exportOptions = {
        resolutions: [
            { label: 'HD (720p)', value: '720p', width: 1280, height: 720 },
            { label: 'Full HD (1080p)', value: '1080p', width: 1920, height: 1080 },
            { label: '2K (1440p)', value: '2k', width: 2560, height: 1440 },
            { label: '4K UHD (2160p)', value: '4k', width: 3840, height: 2160 },
        ],
        framerates: ['24', '25', '30', '60', '120']
    };

    const handleVideoExport = async () => {
        setIsExporting(true);
        setProgress(10); // Start progress
        setError(null);

        try {
            // 1. Collect URLs (and filter out non-video segments if necessary)
            const videoUrls = segments
                .filter(s => (s.activeRevision?.outputAsset?.kind === 'video' && s.activeRevision?.outputAsset?.url) || s.mediaSrc || s.mediaId)
                .map(s => s.activeRevision?.outputAsset?.url || s.mediaSrc || (s.mediaId ? `media:${s.mediaId}` : null))
                .filter(url => url !== null) as string[];

            if (videoUrls.length === 0) {
                throw new Error("Aucun clip vidéo à exporter.");
            }

            console.log('[ExportDialog] Exporting URLs:', videoUrls, 'AI:', aiUpscale);

            const selectedRes = exportOptions.resolutions.find(r => r.value === resolution) || exportOptions.resolutions[1];

            // 2. Call backend fusion
            setProgress(aiUpscale ? 15 : 30); // AI upscale takes longer
            const response = await fetch('/api/video/combine', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    videoUrls,
                    width: selectedRes.width,
                    height: selectedRes.height,
                    fps: parseInt(fps),
                    aiUpscale
                })
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || `Erreur serveur: ${response.statusText}`);
            }

            setProgress(70);

            // 3. Download the result blob
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `studio_jenial_export_${resolution}_${fps}fps_${Date.now()}.mp4`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            setProgress(100);
            setTimeout(() => {
                onClose();
                setIsExporting(false);
                setProgress(0);
            }, 1000);

        } catch (err) {
            console.error('[ExportDialog] Failed:', err);
            setError(err instanceof Error ? err.message : "Erreur inconnue");
            setIsExporting(false);
        }
    };

    const handleConformExport = () => {
        try {
            setIsExporting(true);
            setProgress(50);

            const edlContent = generateEDL(segments, {
                title: "STUDIO_JENIAL_TIMELINE",
                fps: parseInt(edlFps)
            });

            const blob = new Blob([edlContent], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `timeline_${Date.now()}.edl`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            setProgress(100);
            setTimeout(() => {
                onClose();
                setIsExporting(false);
                setProgress(0);
            }, 500);
        } catch (err) {
            setError("Erreur lors de la génération EDL");
            setIsExporting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
            <div className="w-full max-w-lg bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl p-0 overflow-hidden">
                {/* Header */}
                <div className="bg-[#1e1e1e] p-6 border-b border-gray-800">
                    <h2 className="text-2xl font-bold text-white mb-1">Export</h2>
                    <p className="text-gray-400 text-sm">Choississez votre format de sortie.</p>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-800">
                    <button
                        onClick={() => setMode('video')}
                        className={`flex-1 py-3 text-sm font-medium transition-colors ${mode === 'video' ? 'bg-indigo-900/20 text-indigo-400 border-b-2 border-indigo-500' : 'text-gray-500 hover:text-gray-300'
                            }`}
                    >
                        Vidéo (MP4)
                    </button>
                    <button
                        onClick={() => setMode('conform')}
                        className={`flex-1 py-3 text-sm font-medium transition-colors ${mode === 'conform' ? 'bg-indigo-900/20 text-indigo-400 border-b-2 border-indigo-500' : 'text-gray-500 hover:text-gray-300'
                            }`}
                    >
                        Conformation (EDL)
                    </button>
                </div>

                <div className="p-8">
                    {/* === VIDEO EXPORT === */}
                    {mode === 'video' && !isExporting && (
                        <div className="grid grid-cols-2 gap-6 mb-8">
                            {/* Resolution */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Résolution</label>
                                <div className="space-y-2">
                                    {exportOptions.resolutions.map((res) => (
                                        <button
                                            key={res.value}
                                            onClick={() => setResolution(res.value)}
                                            className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${resolution === res.value
                                                ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-900/50'
                                                : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-750 hover:border-gray-600'
                                                }`}
                                        >
                                            <div className="font-bold">{res.label}</div>
                                            {res.value === '4k' && <div className="text-[10px] opacity-70">Upscaling IA Requis (Simulation)</div>}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* FPS */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Images par seconde</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {exportOptions.framerates.map((rate) => (
                                        <button
                                            key={rate}
                                            onClick={() => setFps(rate)}
                                            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${fps === rate
                                                ? 'bg-indigo-600 border-indigo-500 text-white shadow-md'
                                                : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-750'
                                                }`}
                                        >
                                            {rate} FPS
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* AI Upscale Toggle */}
                            {aiUpscaleAvailable && (
                                <div className="col-span-2 mt-2">
                                    <button
                                        onClick={() => setAiUpscale(!aiUpscale)}
                                        className={`w-full px-4 py-4 rounded-xl border-2 transition-all flex items-center gap-4 ${aiUpscale
                                            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 border-purple-500 text-white shadow-lg shadow-purple-900/50'
                                            : 'bg-gray-800/50 border-gray-700 text-gray-300 hover:border-gray-600'
                                            }`}
                                    >
                                        <div className="text-2xl">{aiUpscale ? '✨' : '🎬'}</div>
                                        <div className="text-left flex-1">
                                            <div className="font-bold">
                                                {aiUpscale ? 'IA Super-Résolution Activée' : 'Activer l\'Upscaling IA'}
                                            </div>
                                            <div className="text-xs opacity-70">
                                                {aiUpscale
                                                    ? 'Real-ESRGAN x2 - Qualité Pro (traitement long)'
                                                    : 'Amélioration par modèle Real-ESRGAN'
                                                }
                                            </div>
                                        </div>
                                        <div className={`w-12 h-6 rounded-full transition-all ${aiUpscale ? 'bg-purple-300' : 'bg-gray-600'}`}>
                                            <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-all ${aiUpscale ? 'translate-x-6' : 'translate-x-0.5'} mt-0.5`}></div>
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* === CONFORM EXPORT === */}
                    {mode === 'conform' && !isExporting && (
                        <div className="mb-8 space-y-4">
                            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                                <h3 className="font-bold text-blue-300 mb-2 flex items-center gap-2">
                                    <span className="material-symbols-outlined">settings_ethernet</span>
                                    Workflow DaVinci / Premiere
                                </h3>
                                <p className="text-sm text-gray-300 mb-3">
                                    L'export EDL (CMX 3600) génère un fichier texte décrivant votre montage.
                                    Importez-le dans votre logiciel de montage pro pour relier vos fichiers sources.
                                </p>
                                <ul className="text-xs text-gray-400 space-y-1 list-disc pl-4">
                                    <li>Format: CMX 3600</li>
                                    <li>Mode: Non-Drop Frame</li>
                                    <li>Reel Name par défaut: "GENAI"</li>
                                </ul>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Frame Rate du Projet</label>
                                <div className="flex gap-2">
                                    {['24', '25', '30'].map((rate) => (
                                        <button
                                            key={rate}
                                            onClick={() => setEdlFps(rate)}
                                            className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${edlFps === rate
                                                ? 'bg-blue-600 border-blue-500 text-white shadow-md'
                                                : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-750'
                                                }`}
                                        >
                                            {rate}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="mb-4 p-3 bg-red-900/50 border border-red-500/50 rounded-lg text-red-200 text-sm">
                            {error}
                        </div>
                    )}

                    {isExporting ? (
                        <div className="space-y-4 py-8">
                            <div className="w-full bg-gray-800 rounded-full h-3 overflow-hidden">
                                <div
                                    className={`h-full transition-all duration-300 relative ${mode === 'video' ? 'bg-gradient-to-r from-indigo-500 to-purple-500' : 'bg-gradient-to-r from-blue-500 to-cyan-500'}`}
                                    style={{ width: `${progress}%` }}
                                >
                                    <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                                </div>
                            </div>
                            <p className="text-center text-sm text-indigo-300 font-medium animate-pulse">
                                {mode === 'video' ? `Rendu Vidéo ${resolution}...` : "Génération EDL..."}
                            </p>
                        </div>
                    ) : (
                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-800">
                            <button
                                onClick={onClose}
                                className="px-5 py-2.5 text-gray-400 hover:text-white transition-colors"
                            >
                                Annuler
                            </button>
                            {mode === 'video' ? (
                                <button
                                    onClick={handleVideoExport}
                                    className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-indigo-600/20 hover:shadow-indigo-600/40 flex items-center gap-2"
                                >
                                    <span className="material-symbols-outlined">movie</span>
                                    Exporter MP4
                                </button>
                            ) : (
                                <button
                                    onClick={handleConformExport}
                                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40 flex items-center gap-2"
                                >
                                    <span className="material-symbols-outlined">description</span>
                                    Télécharger EDL
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
